import { GraphQLClient, ClientError } from "graphql-request";
import { useAuthStore } from "@/stores/auth-store";
import { toast } from "sonner";

const GRAPHQL_ENDPOINT = import.meta.env.VITE_GRAPHQL_ENDPOINT || "http://127.0.0.1:8080/graphql";

const AUTH_ERROR_KEYWORDS = ["unauthorized", "jwt expired", "invalid token"];

export function isAuthError(response: unknown): boolean {
  if (response instanceof ClientError) {
    const status = response.response.status;
    if (status === 401 || status === 403) return true;

    const message = response.message.toLowerCase();
    return AUTH_ERROR_KEYWORDS.some((keyword) => message.includes(keyword));
  }

  if (response instanceof Error) {
    const message = response.message.toLowerCase();
    return AUTH_ERROR_KEYWORDS.some((keyword) => message.includes(keyword));
  }

  return false;
}

export function handleAuthError(response: unknown): void {
  if (!isAuthError(response)) return;

  const { accessToken, clearAuth } = useAuthStore.getState();
  if (!accessToken) return;

  clearAuth();
  toast.error("인증이 만료되었습니다. 다시 로그인해주세요.");
  window.location.href = "/login";
}

export const graphqlClient = new GraphQLClient(GRAPHQL_ENDPOINT, {
  responseMiddleware: handleAuthError,
});

export async function authenticatedRequest<T>(document: string, variables?: Record<string, unknown>): Promise<T> {
  const accessToken = useAuthStore.getState().getAccessToken();
  const requestHeaders = accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined;
  try {
    return await graphqlClient.request<T>(document, variables, requestHeaders);
  } catch (error) {
    // ClientError.message 는 응답/쿼리 원문 JSON 전체를 포함해 ErrorBoundary/
    // 토스트에 그대로 덤프된다 — 첫 GraphQL 에러 메시지만 남겨 사람이 읽게 한다.
    // (인증 오류 처리는 responseMiddleware(handleAuthError)가 이미 원본으로 수행)
    if (error instanceof ClientError) {
      const message =
        error.response.errors?.[0]?.message ?? "요청에 실패했습니다";
      throw new Error(message, { cause: error });
    }
    throw error;
  }
}
